import { useState } from 'react';
import { Navbar } from '@/components/Navbar';
import { Footer } from '@/components/Footer';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import {
  Car,
  MapPin,
  Plane,
  Clock,
  Users,
  Shield,
  CheckCircle2,
  ArrowRight,
  Bus,
  ShieldCheck,
  Globe
} from 'lucide-react';
import { Link } from 'react-router-dom';
import SEO from '@/components/SEO';

const TransferPage = () => {
  const transfers = [
    {
      id: 1,
      name: 'Victoria Falls Airport Transfer',
      route: 'VFA Airport ↔ Vic Falls Hotels',
      description: 'Reliable and comfortable shuttle service between Victoria Falls International Airport and all major hotels in Victoria Falls town.',
      duration: '20-30 mins',
      capacity: '1-7 People',
      price: '$15',
      type: 'Shared/Private',
      image: '/images/transfer-team.jpg',
      category: 'Airport Transfers',
      location: 'Zimbabwe'
    },
    {
      id: 2,
      name: 'Livingstone Airport Transfer',
      route: 'LVI Airport ↔ Livingstone Hotels',
      description: 'Professional transfer service from Harry Mwanga Nkumbula International Airport to your accommodation in Livingstone.',
      duration: '15-25 mins',
      capacity: '1-7 People',
      price: '$20',
      type: 'Shared/Private',
      image: '/images/transfer-team.jpg',
      category: 'Airport Transfers',
      location: 'Zambia'
    },
    {
      id: 3,
      name: 'Kasane Airport Transfer',
      route: 'BBK Airport ↔ Kasane Hotels',
      description: 'Direct transfers from Kasane International Airport to hotels and lodges in the Kasane/Chobe area.',
      duration: '10-20 mins',
      capacity: '1-7 People',
      price: '$25',
      type: 'Private',
      image: '/images/transfer-team.jpg',
      category: 'Airport Transfers',
      location: 'Botswana'
    },
    {
      id: 4,
      name: 'Inter-City: Vic Falls to Livingstone',
      route: 'Vic Falls ↔ Livingstone (Cross-Border)',
      description: 'Seamless cross-border transfer between Victoria Falls (Zimbabwe) and Livingstone (Zambia). Includes assistance with immigration.',
      duration: '45-60 mins',
      capacity: '1-7 People',
      price: '$30',
      type: 'Private',
      image: '/images/transfer-interior.jpg',
      category: 'Inter-City Transfers',
      location: 'Zim-Zam Border'
    },
    {
      id: 5,
      name: 'Victoria Falls to Kasane',
      route: 'Vic Falls ↔ Kasane/Chobe',
      description: 'Convenient cross-border transfer to the gateway of Chobe National Park. Includes border crossing assistance.',
      duration: '1.5 - 2 hours',
      capacity: '1-7 People',
      price: '$50',
      type: 'Private',
      image: '/images/transfer-interior.jpg',
      category: 'Inter-City Transfers',
      location: 'Zim-Bots Border'
    },
    {
      id: 6,
      name: 'Victoria Falls to Hwange',
      route: 'Vic Falls ↔ Hwange National Park',
      description: 'Comfortable long-distance transfer to Zimbabwe\'s premier wildlife destination.',
      duration: '2.5 - 3 hours',
      capacity: '1-7 People',
      price: '$120',
      type: 'Private',
      image: '/images/transfer-trailers.jpg',
      category: 'Inter-City Transfers',
      location: 'Zimbabwe'
    }
  ];

  const features = [
    {
      icon: ShieldCheck,
      title: 'Safe & Reliable',
      description: 'Fully licensed vehicles and professional, experienced drivers.'
    },
    {
      icon: Clock,
      title: 'Punctual Service',
      description: 'We monitor flight schedules to ensure timely pickups.'
    },
    {
      icon: Users,
      title: 'Meet & Greet',
      description: 'Personalized welcome at the airport with name signage.'
    },
    {
      icon: Globe,
      title: 'Cross-Border Experts',
      description: 'Hassle-free assistance with immigration and customs.'
    }
  ];

  const [activeCategory, setActiveCategory] = useState('All');
  const categories = ['All', 'Airport Transfers', 'Inter-City Transfers'];

  const filteredTransfers = activeCategory === 'All'
    ? transfers
    : transfers.filter(t => t.category === activeCategory);

  return (
    <div className="min-h-screen">
      <SEO
        title="Transfers & Shuttles"
        description="Reliable airport transfers and shuttles in Victoria Falls, Livingstone, and Kasane. Professional drivers and comfortable vehicles."
        keywords="Victoria Falls Airport Transfer, Livingstone Airport Transfer, Kasane Transfer, Chobe Shuttle, Taxi Victoria Falls"
        canonicalUrl="https://www.peregrinetoursandsafaris.com/transfers"
        schema={{
          "@context": "https://schema.org",
          "@type": "ItemList",
          "name": "African Safari Transfers and Shuttles",
          "description": "Professional airport and inter-city transfer services between major hubs in Southern Africa.",
          "itemListElement": transfers.map((transfer, index) => ({
            "@type": "ListItem",
            "position": index + 1,
            "item": {
              "@type": "Service",
              "name": transfer.name,
              "description": transfer.description,
              "provider": {
                "@type": "Organization",
                "name": "Peregrine Tours & Safaris"
              },
              "areaServed": [
                {
                  "@type": "Place",
                  "name": transfer.location
                }
              ],
              "offers": {
                "@type": "Offer",
                "price": transfer.price.replace(/[^0-9.]/g, ''),
                "priceCurrency": "USD"
              }
            }
          }))
        }}
      />

      <Navbar />

      {/* Hero Section */}
      <section className="relative pt-32 pb-16 bg-gradient-to-b from-muted/30 to-background">
        <div className="container-custom text-center">
          <span className="inline-block px-4 py-1 bg-primary/10 text-primary text-sm font-medium rounded-full mb-4">
            Reliable Transportation
          </span>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-foreground mb-6 font-serif">
            Professional Multi-Border Transfers
          </h1>
          <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
            Safe, comfortable, and reliable transfer services across Zimbabwe, Zambia, and Botswana. From airport pickups to inter-city journeys.
          </p>
        </div>
      </section>

      {/* Stats/Features Section */}
      <section className="py-12 bg-background border-y border-border">
        <div className="container-custom">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            {features.map((feature, index) => (
              <div key={index} className="flex flex-col items-center text-center space-y-2">
                <div className="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center mb-2">
                  <feature.icon className="w-6 h-6 text-primary" />
                </div>
                <h3 className="font-bold text-sm uppercase tracking-wider">{feature.title}</h3>
                <p className="text-xs text-muted-foreground">{feature.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Category Filter */}
      <section className="py-8 bg-muted/20">
        <div className="container-custom flex justify-center gap-4">
          {categories.map((category) => (
            <button
              key={category}
              onClick={() => setActiveCategory(category)}
              className={`px-6 py-2 rounded-full text-sm font-medium transition-all duration-300 ${activeCategory === category
                ? 'bg-primary text-primary-foreground shadow-lg'
                : 'bg-background text-muted-foreground hover:bg-primary/10 hover:text-primary'
                }`}
            >
              {category}
            </button>
          ))}
        </div>
      </section>

      {/* Transfers Grid */}
      <section className="section-padding bg-background">
        <div className="container-custom">
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {filteredTransfers.map((transfer) => (
              <Card key={transfer.id} className="group overflow-hidden border-2 hover:border-primary/30 transition-all duration-300 flex flex-col">
                <div className="relative h-48 overflow-hidden bg-muted">
                  <img src={transfer.image} alt={transfer.name} className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110" />
                  <div className="absolute top-4 left-4 bg-background/95 backdrop-blur-sm rounded-full px-3 py-1 flex items-center gap-2 border border-border z-10">
                    <MapPin className="w-3 h-3 text-primary" />
                    <span className="text-[10px] font-bold text-foreground uppercase tracking-wider">{transfer.location}</span>
                  </div>
                </div>

                <div className="p-6 flex-1 flex flex-col">
                  <div className="flex justify-between items-start mb-4">
                    <div>
                      <h3 className="text-xl font-bold text-foreground font-serif leading-tight group-hover:text-primary transition-colors">
                        {transfer.name}
                      </h3>
                      <p className="text-xs font-medium text-primary mt-1">{transfer.route}</p>
                    </div>
                  </div>

                  <p className="text-muted-foreground text-sm leading-relaxed mb-6 flex-1">
                    {transfer.description}
                  </p>

                  <div className="grid grid-cols-2 gap-4 mb-6">
                    <div className="flex items-center gap-2 text-[10px] font-bold uppercase tracking-widest text-muted-foreground">
                      <Clock className="w-3 h-3" />
                      <span>{transfer.duration}</span>
                    </div>
                    <div className="flex items-center gap-2 text-[10px] font-bold uppercase tracking-widest text-muted-foreground">
                      <Users className="w-3 h-3" />
                      <span>{transfer.capacity}</span>
                    </div>
                  </div>

                  <div className="flex items-center justify-between py-4 border-t border-border mt-auto">
                    <div className="flex flex-col">
                      <span className="text-2xl font-black text-primary">{transfer.price}</span>
                      <span className="text-[9px] text-muted-foreground uppercase">Per Person / One Way</span>
                    </div>
                    <Button asChild className="bg-primary hover:bg-primary-dark text-primary-foreground px-6 shadow-md hover:shadow-lg transition-all">
                      <a href={`mailto:reservations@peregrinetoursandsafaris.com?subject=Transfer Booking Inquiry: ${encodeURIComponent(transfer.name)}`}>
                        Book Now
                      </a>
                    </Button>
                  </div>
                </div>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="section-padding bg-muted/30">
        <div className="container-custom text-center">
          <div className="max-w-3xl mx-auto space-y-6">
            <h2 className="text-3xl font-bold font-serif">Need a Custom Transfer?</h2>
            <p className="text-muted-foreground">
              We provide private transfers to any destination in Zimbabwe, Zambia, and Botswana. Contact us for a personalized quote for your group.
            </p>
            <Button asChild variant="outline" size="lg" className="rounded-full border-primary text-primary hover:bg-primary hover:text-white">
              <Link to="/contact">
                Get a Quote
                <ArrowRight className="ml-2 w-5 h-5" />
              </Link>
            </Button>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default TransferPage;
